// US States for dropdown
export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
];

// Countries — ISO 3166-1 alpha-2 codes, in the order we expect to see them
// (US first as the default; then the markets we actually sell into / import
// from in Zoho Books). Not exhaustive — falls back to plain text entry when
// a country isn't in this list, since the country field is ultimately just
// a string column.
export const COUNTRIES: Array<{ code: string; name: string }> = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'PT', name: 'Portugal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'GR', name: 'Greece' },
  { code: 'TR', name: 'Türkiye' },
  { code: 'IL', name: 'Israel' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'IN', name: 'India' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'CN', name: 'China' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'SG', name: 'Singapore' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'ZA', name: 'South Africa' },
];

// Build a lowercased name→code map plus a tolerant fallback map so we can
// turn whatever Zoho hands us ("Italy", "ITALY", "italy") into the 2-letter
// code. Also accepts common alternative spellings.
const _COUNTRY_NAME_TO_CODE: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const c of COUNTRIES) map[c.name.toLowerCase()] = c.code;
  // Common alternative spellings / older names.
  map["usa"] = "US";
  map["united states of america"] = "US";
  map["u.s.a."] = "US";
  map["u.s."] = "US";
  map["uk"] = "GB";
  map["great britain"] = "GB";
  map["england"] = "GB";
  map["scotland"] = "GB";
  map["wales"] = "GB";
  map["northern ireland"] = "GB";
  map["holland"] = "NL";
  map["turkey"] = "TR";
  map["czech republic"] = "CZ";
  map["south korea"] = "KR";
  map["republic of korea"] = "KR";
  map["uae"] = "AE";
  return map;
})();

/** Best-effort lookup: returns the 2-letter ISO code for a country name, or
 *  null if we don't recognize it. Case-insensitive; trims whitespace. */
export function lookupCountryCode(name: string | null | undefined): string | null {
  if (!name) return null;
  const trimmed = String(name).trim();
  if (!trimmed) return null;
  if (trimmed.length === 2) {
    const upper = trimmed.toUpperCase();
    if (COUNTRIES.some((c) => c.code === upper)) return upper;
  }
  return _COUNTRY_NAME_TO_CODE[trimmed.toLowerCase()] ?? null;
}

/** Returns the display name for a 2-letter ISO code, or the code itself if
 *  it isn't in our list (we still want SOMETHING to show on a cert). */
export function countryName(code: string | null | undefined): string {
  if (!code) return "";
  const upper = String(code).trim().toUpperCase();
  const found = COUNTRIES.find((c) => c.code === upper);
  return found?.name ?? upper;
}

// License Products
export const LICENSE_PRODUCTS = [
  'Fresh Intranet',
  'LightPath Intranet',
  'Omnia Intranet',
  'Orchestry',
  'Custom', // Allow custom entry
];

// License Contract Terms
export const LICENSE_TERMS = [
  '1-year',
  '2-year',
  '3-year',
  '4-year',
  '5-year',
];

// Payment Milestone Options
export const PAYMENT_MILESTONE_OPTIONS = [
  { value: 2, label: '2 Milestones' },
  { value: 3, label: '3 Milestones (Standard)' },
  { value: 4, label: '4 Milestones' },
];

// Standard Project Milestones (based on user's workflow)
export const PROJECT_MILESTONES = [
  {
    title: 'Kick Off',
    description: 'Project initiation, team alignment, and schedule confirmation',
  },
  {
    title: 'Workshops / Discovery',
    description: 'Completion of all discovery sessions and requirements documentation',
  },
  {
    title: 'Implementation Complete',
    description: 'Configuration, internal testing, and stakeholder review finalized',
  },
  {
    title: 'Cutover & Closeout',
    description: 'Launch support completed, project closure, and final review',
  },
];

